// src/firebaseConfig.js
import { initializeApp } from "firebase/app";
import {
  initializeAuth,
  setPersistence,
  browserLocalPersistence,
  browserPopupRedirectResolver,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  FacebookAuthProvider,
  OAuthProvider,
} from "firebase/auth";
import { getFirestore } from "firebase/firestore";

const firebaseConfig = {
  apiKey: "AIzaSyDikxRBbD7PUDdXfVg23hEd7-Z2dOb_RTw",
  authDomain: "dresscode-a265e.firebaseapp.com",  // NE JAMAIS CHANGER ÇA !
  projectId: "dresscode-a265e",
  storageBucket: "dresscode-a265e.firebasestorage.app",
  messagingSenderId: "711598413651",
  appId: "1:711598413651:web:c3b1de7ac8a52a8296f3a6",
};

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
// Utilise initializeAuth pour fournir un resolver robuste (Safari/iOS)
const auth = initializeAuth(app, {
  persistence: [browserLocalPersistence],
  popupRedirectResolver: browserPopupRedirectResolver,
});

// Session persistante - Force localStorage (plus fiable que cookies sur Safari)
// CRITIQUE pour PWA : Sans cela, l'utilisateur sera déconnecté à chaque fermeture
setPersistence(auth, browserLocalPersistence)
  .then(() => {
    if (process.env.NODE_ENV !== 'production') {
      console.log("Firebase persistence configured: browserLocalPersistence");
    }
  })
  .catch((e) => {
    // Keep errors for troubleshooting but avoid verbose stacks in prod
    console.error("Firebase persistence failed:", e?.code || e?.message || e);
  });

// -------- Providers
const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: "select_account" });

const facebookProvider = new FacebookAuthProvider();
facebookProvider.addScope("email");

const appleProvider = new OAuthProvider("apple.com");
appleProvider.addScope("email");
appleProvider.addScope("name");

// Détection plateforme/navigateur
const ua = navigator.userAgent || "";
const isIOS = /iPad|iPhone|iPod/.test(ua) || (ua.includes("Mac") && 'ontouchend' in document);
const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
const isMobile = /Mobi|Android/i.test(ua) || isIOS;

async function signWith(provider) {
  try {
    // Détection localhost à chaque appel (plus fiable)
    const hostname = window.location.hostname;
    const isLocalhost = hostname === 'localhost' || 
                       hostname === '127.0.0.1' || 
                       hostname.includes('localhost') ||
                       hostname.includes('192.168.') || // IP locale
                       hostname.endsWith('.local'); // mDNS
    
    if (process.env.NODE_ENV !== 'production') {
      console.log("[AUTH] Starting OAuth sign in", {
        provider: provider.providerId,
        hostname,
        isMobile,
        isIOS,
        isSafari,
        isLocalhost,
        timestamp: new Date().toISOString(),
      });
    }
    
    // TEMPORAIRE : Forcer popup PARTOUT pour tester si ça résout le problème localStorage
    // Safari bloque le localStorage cross-domain, donc le redirect ne fonctionne pas
    const shouldUseRedirect = false; // Désactivé temporairement pour tester popup sur mobile
    
    if (process.env.NODE_ENV !== 'production') console.log("[AUTH] shouldUseRedirect =", shouldUseRedirect);
    
    if (shouldUseRedirect) {
      if (process.env.NODE_ENV !== 'production') console.log("✅ Using signInWithRedirect (iOS Safari)");
      
      // Stocker des infos avant le redirect (pour debug)
      try {
        sessionStorage.setItem('oauth_redirect_start', new Date().toISOString());
        sessionStorage.setItem('oauth_redirect_url', window.location.href);
        sessionStorage.setItem('oauth_provider', provider.providerId);
        if (process.env.NODE_ENV !== 'production') console.log("Stored redirect info in sessionStorage");
      } catch (e) {
        if (process.env.NODE_ENV !== 'production') console.error("Could not store redirect info:", e);
      }
      
      if (process.env.NODE_ENV !== 'production') console.log("🔄 Calling signInWithRedirect...");
      await signInWithRedirect(auth, provider);
      if (process.env.NODE_ENV !== 'production') console.log("⏳ Redirect initiated (you should be redirected now)");
      return null;
    } else {
      if (process.env.NODE_ENV !== 'production') console.log(`Using signInWithPopup (${isLocalhost ? 'localhost' : 'desktop/other-browser'})`);
      const cred = await signInWithPopup(auth, provider);
      if (process.env.NODE_ENV !== 'production') console.log("Popup success:", cred);
      return cred;
    }
  } catch (e) {
    console.error("OAuth error details:", {
      code: e.code,
      message: e.message,
      email: e.email,
      credential: e.credential
    });
    throw e;
  }
}

/** A appeler dans Login/Signup au mount pour récupérer un éventuel résultat redirect */
let redirectResultPromise = null; // Cache pour éviter les appels multiples

async function resolveRedirectResult() {
  try {
    // Si déjà appelé, retourner la même promesse (évite les appels multiples)
    if (redirectResultPromise) {
      if (process.env.NODE_ENV !== 'production') console.log("[AUTH] Using cached redirect result promise (already called)");
      return redirectResultPromise;
    }
    
    if (process.env.NODE_ENV !== 'production') {
      console.log("[AUTH] CHECKING FOR REDIRECT RESULT", {
        timestamp: new Date().toISOString(),
        url: window.location.href,
        pathname: window.location.pathname,
        referrer: document.referrer || 'EMPTY',
        origin: window.location.origin,
      });
    }
    
    // SessionStorage debug
    try {
      const ssKeys = Object.keys(sessionStorage || {});
      if (process.env.NODE_ENV !== 'production') {
        console.log("[AUTH] SessionStorage keys:", ssKeys.length);
      }
      
      // Afficher les valeurs Firebase importantes
      ssKeys.forEach(key => {
        if (key.includes('firebase') || key.includes('oauth') || key.includes('dc_')) {
          try {
            const value = sessionStorage.getItem(key);
            if (process.env.NODE_ENV !== 'production') console.log(`  ${key}:`, value ? value.substring(0, 100) + '...' : 'null');
          } catch {}
        }
      });
      
      // Info stockée avant redirect
      const redirectStart = sessionStorage.getItem('oauth_redirect_start');
      const redirectUrl = sessionStorage.getItem('oauth_redirect_url');
      const provider = sessionStorage.getItem('oauth_provider');
      
      if (redirectStart) {
      if (process.env.NODE_ENV !== 'production') {
        console.log("[AUTH] Redirect Info", {
          startedAt: redirectStart,
          fromUrl: redirectUrl,
          provider,
          elapsed: ((Date.now() - new Date(redirectStart).getTime()) / 1000).toFixed(2),
        });
      }
    } else {
      if (process.env.NODE_ENV !== 'production') console.log("[AUTH] No redirect start info in sessionStorage");
    }
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') console.error("Error reading sessionStorage:", e);
  }
    
    // LocalStorage debug
    try {
      const lsKeys = Object.keys(localStorage || {});
      if (process.env.NODE_ENV !== 'production') {
        console.log("[AUTH] LocalStorage keys:", lsKeys.length);
      }
      const firebaseKeys = lsKeys.filter(k => k.includes('firebase'));
      if (process.env.NODE_ENV !== 'production') console.log("[AUTH] Firebase keys:", firebaseKeys);
      firebaseKeys.forEach(key => {
        try {
          const value = localStorage.getItem(key);
          if (process.env.NODE_ENV !== 'production') console.log(`  ${key}:`, value ? value.substring(0, 100) + '...' : 'null');
        } catch {}
      });
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') console.error("Error reading localStorage:", e);
    }
    
    if (process.env.NODE_ENV !== 'production') console.log("[AUTH] Calling Firebase getRedirectResult()...");
    
    redirectResultPromise = getRedirectResult(auth);
    const result = await redirectResultPromise;
    
    if (process.env.NODE_ENV !== 'production') {
      console.log("[AUTH] getRedirectResult completed");
      console.log("Result:", !!result);
    }
    
    if (result) {
      if (process.env.NODE_ENV !== 'production') {
        console.log("[AUTH] User details present; provider:", result.providerId);
      }
    } else {
      if (process.env.NODE_ENV !== 'production') {
        console.log("[AUTH] No redirect result; potential issues with cookies/localStorage or config");
      }
    }
    
    // Nettoyer après 5 secondes (pour permettre les nouveaux redirects)
    setTimeout(() => {
      redirectResultPromise = null;
    }, 5000);
    
    return result; // null si pas de redirect
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') {
      console.error("[AUTH] getRedirectResult error:", e);
    }
    redirectResultPromise = null; // Reset en cas d'erreur
    throw e;
  }
}

// Exports
export {
  app,
  db,
  auth,
  googleProvider,
  facebookProvider,
  appleProvider,
  signWith,
  resolveRedirectResult,
};
